import { Component, DestroyRef, Input, inject, signal } from '@angular/core';
import { EntrySelectors } from 'src/app/state/entry.selectors';
import { Store } from '@ngrx/store';
import { combineLatest, filter, map, take, tap } from 'rxjs';
import { EntryService } from '../entry.service';
import { GoogleAnalyticsService } from 'src/app/google-analytics.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { PlatformService } from 'src/app/platform.service';
import { DownloadService } from 'src/app/download-service';
import { UtilService } from 'src/app/util.service';
import { EntryData, PROVIDER_MAP, ProviderData } from '../structure-data.models';
@Component({
  selector: 'entry-page-header',
  templateUrl: './entry-page-header.component.html',
  styleUrl: './entry-page-header.component.css',
  standalone: false
})
export class EntryPageHeaderComponent {
  public accession = toSignal(this.store.select(EntrySelectors.accession));
  // public entryData = toSignal(this.store.select(EntrySelectors.entryData));
  // public uniprotData = toSignal(this.store.select(EntrySelectors.uniprotData));
  public dropdownOptions: { label: string, value?: any, isShow?: boolean, isHeader?: boolean, isWarning?:boolean }[];
  public readonly downloadService = inject(DownloadService);
  public isOldUniprotEntry: boolean = false;
  public entryData = signal<EntryData>({} as EntryData);
  public uniprotData = signal<any>({});
  public providerData = signal<ProviderData>({} as ProviderData);
  private readonly destroyRef = inject(DestroyRef);
  public proteinTitle: string = "";
  public providerDataTemp = { providerName: '' }; 
  public providerName:string = "";
  constructor(
    private store: Store,
    private utilService: UtilService,
    private entryService: EntryService,
    private gaService: GoogleAnalyticsService,
    private platform: PlatformService
  ) {}

  async ngOnInit(): Promise<void> {
    combineLatest([
      this.store.select(EntrySelectors.entryData),
      this.store.select(EntrySelectors.uniprotData),
      this.store.select(EntrySelectors.providerData),
    ])
      .pipe(
        filter(([entryData, uniprotData, providerData]) =>
          !!entryData &&
          !!providerData 
        ),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(([entryData, uniprotData, providerData]) => {
        this.entryData.set(entryData);
        this.uniprotData.set(uniprotData);
        this.providerData.set(providerData);
        this.proteinTitle = entryData?.uniprotDescription ?? "Not named";
        this.setDropdownOptions();
      });
  }

  setDropdownOptions(){
    if(this.entryData().amAnnotationsUrl){
      this.dropdownOptions = [
        { label: 'Model data', isHeader: true },
        { label: 'mmCIF file', value: "mmcif", isShow: true},
        { label: 'PDB file', value: "pdbfile", isShow: true},
        { label: 'Predicted Aligned Error', value: "paefile", isShow: true},
        { label: 'Multiple Sequence Alignment (MSA)', value: "msaUrl", isShow: this.entryData().msaUrl ? true : false},
        { label: '', isWarning: true},
        { label: 'AlphaMissense data', isHeader: true},
        { label: 'Heatmap data', value: "heatmapdata" , isShow: this.entryData().amAnnotationsUrl ? true : false},
        { label: 'HG19 pathogenicity scores', value: "hg19", isShow: this.entryData().amAnnotationsHg19Url ? true : false },
        { label: 'HG38 pathogenicity scores', value: "hg38", isShow: this.entryData().amAnnotationsHg38Url ? true : false },
      ];
    }else{
      this.dropdownOptions = [
        { label: 'Model data', isHeader: true },
        { label: 'mmCIF file', value: "mmcif", isShow: true},
        { label: 'PDB file', value: "pdbfile", isShow: true},
        { label: 'Predicted Aligned Error', value: "paefile", isShow: true},
        { label: 'Multiple Sequence Alignment (MSA)', value: "msaUrl", isShow: this.entryData().msaUrl ? true : false},
        { label: 'More download data coming soon', isWarning: true},
      ];
    }
    // if(this.providerData().providerName === 'Google DeepMind' && this.entryData().isUniProt){
    //   if((this.entryData().sequenceVersionDate!= "" || this.entryData().sequenceVersionDate!= null) && (this.uniprotData().sequence.modified != "" || this.uniprotData().sequence.modified != null)){
    //     this.isOldUniprotEntry = this.utilService.areDatesDifferent(this.entryData().sequenceVersionDate, this.uniprotData().sequence.modified);
    //   }
    // }

    this.providerName = PROVIDER_MAP[this.entryData().providerId];
    // this.providerDataTemp = {
    //   providerName: providerName?? 'Unknown Provider'
    // }
  }
  convertFormAFDBIds(ids: string[]): string[] {
    return ids.map(id => {
      // Remove heading 'AF-' and any trailing part starting with '-F1'
      return id.replace(/^AF-/, '').replace(/-F1.*$/, '');
    });
  }
  handleSelection(value: string) {
    const urlMap = {
      mmcif: {url: this.entryData().cifUrl, tag: "cifUrl", msg: "Clicks on downloading MMCIF  file"},
      pdbfile: {url: this.entryData().pdbUrl, tag: "pdbUrl", msg: "Clicks on downloading PDB file"},
      paefile: {url: this.entryData().paeDocUrl, tag: "paeDocUrl", msg: "Clicks on downloading PAE file"},
      msaUrl: {url: this.entryData().msaUrl, tag: "msaUrl", msg: "Clicks on downloading MSA file"},
      heatmapdata: {url: this.entryData().amAnnotationsUrl, tag: "AM_heatmap_download", msg: "Clicks on downloading AM heatmap csv"},
      hg19: {url: this.entryData().amAnnotationsHg19Url, tag: "AM_hg19_download", msg: "Clicks on downloading AM hg19 csv"},
      hg38: {url: this.entryData().amAnnotationsHg38Url, tag: "AM_hg38_download", msg: "Clicks on downloading AM hg38 csv"}
    };
  
    const downloadUrl = urlMap[value].url;

    if(value === 'heatmapdata' || value === 'hg19' || value === 'hg38'){
      if (downloadUrl) { 
        this.downloadFile(downloadUrl);
      }
    }
    if(value === 'mmcif' || value === 'pdbfile' || value === 'paefile' || value === 'msaUrl'){
      if (downloadUrl) { 
        this.downloadFile(downloadUrl);
      }
    }

    this.gaService.eventEmitter(
      urlMap[value].tag,
      'AlphaMissense',
      'click',
      urlMap[value].tag,
      urlMap[value].msg
    );

    // Do something with the selected value
  }
  downloadFile(url) {
    const platformdocument = this.platform.getDocument();
    const link = platformdocument.createElement('a');
    link.href = url;
    platformdocument.body.appendChild(link);
    link.click();
    this.platform.getDocument().body.removeChild(link);
    const windowWithURL = this.platform.getWindow() as Window & { URL: typeof URL };

    windowWithURL.URL.revokeObjectURL(url);
  }
}
